import React, { useCallback, useEffect } from 'react';
import useLeverageSliderEngine from 'src/components/TradeForm/LeverageSliderInput/hooks/useLeverageSliderEngine';
import Slider from 'src/components/primitives/Slider/Slider';

interface Props {
	currentLeverage: number;
	maxLeverage: number;
	targetTradeQuoteSize: number;
	totalCurrentPositionSize: number;
	maxQuoteSize: number;
	targetMarketCurrentPositionSize: number;
	sameSide: boolean;
	onCurrentSizeChange: (quoteSize: number) => void;
}

const SpotLeverageSliderInput = ({
	currentLeverage,
	maxLeverage,
	targetTradeQuoteSize,
	totalCurrentPositionSize,
	targetMarketCurrentPositionSize: targetCurrentPositionSize,
	maxQuoteSize,
	sameSide,
	onCurrentSizeChange,
}: Props) => {
	const engineValues = useLeverageSliderEngine({
		totalCurrentPosition: totalCurrentPositionSize,
		currentLeverage: currentLeverage,
		maxQuote: maxQuoteSize,
		currentPositionQuote: targetCurrentPositionSize,
		maxLeverage,
		currentQuote: targetTradeQuoteSize,
		sameSide,
	});

	const handleSliderChange = useCallback(
		(sliderPct: number) => {
			if (maxQuoteSize === 0) {
				return;
			}

			const amount = engineValues.availableLeveragePoints * (sliderPct / 100);

			onCurrentSizeChange(
				engineValues.calcNewQuoteFromLeveragePoint({
					newPt: amount,
				})
			);
		},
		[engineValues, maxQuoteSize, onCurrentSizeChange]
	);

	useEffect(() => {
		if (targetTradeQuoteSize > maxQuoteSize) {
			handleSliderChange(100);
		}
	}, [targetTradeQuoteSize, maxQuoteSize, handleSliderChange]);

	const showDefault = targetTradeQuoteSize <= 0;

	const percentage = Math.abs(
		(engineValues.currentLeveragePoint / engineValues.availableLeveragePoints) *
			100
	);
	// Round it so the slider doesn't update due to max collateral fluctuations.
	const sliderValue = Math.round(showDefault ? 0 : Math.min(percentage, 100));

	const value = Math.min(Math.max(0, sliderValue), 100);

	return (
		<Slider
			value={value}
			onValueChange={handleSliderChange}
			onValueCommit={handleSliderChange}
		/>
	);
};

export default React.memo(SpotLeverageSliderInput);
